// The document: what file the drawing is (its saved name, its cloud row), New /
// Open / Save, the recovery snapshot, other tabs on the same file, and the cloud
// projects (the web build's Save / Load). docs/claude/platform.md.
import {
  type CadUser,
  type Drawing,
  type ProductCategory,
  type SavedProjectSummary,
} from '../../shared/types';
import {
  flashStatus,
  invalidate,
  layers,
  leaveEdit,
  pushHistory,
  redrawBackdrop,
  renderProperties,
  state,
} from './core';
import {
  showAlert,
  showConfirm,
  showPrompt,
} from '../dialog';
import { drawingToDxf } from '../dxfExport';
import { tr } from '../i18n';
import { exportBaseName } from '../naming';
import { partsTab } from './partsTab';
import { rasterizePdfPage } from '../pdf';
import { platform } from '../platform';
import { renderPreview } from '../print';
import {
  clearRecovery,
  deleteDraft,
  flushRecovery,
  initRecovery,
  listAllDrafts,
  noteRecoveryChange,
  type RecoverySnapshot,
  recoveryTabId,
} from '../recovery';
import {
  $,
  amCommerciale,
  applyLoadedDrawing,
  bgImageMap,
  bumpBackground,
  canEditStandards,
  CATEGORY_DISCIPLINE,
  deselectAll,
  drainInstance,
  importedHidden,
  isDisciplineHidden,
  myAreaManagerId,
  myUserId,
  rcpPaintOf,
  resetViewTabs,
  sectionSources,
  setTool,
  updateTitle,
} from '../main';
import {
  activeSheet,
  modelBoundsOrNull,
  sheetPreviewDataUrl,
} from './sheetSpace';

/** This module's state that main.ts reads and writes too. */
export const doc = {
  // The saved/opened project's name (no extension), used as the print job name.
  savedProjectName: null as string | null,
};

// The cloud file (Baserow row id) the current drawing came from / was last saved
// to, so re-saving updates that row instead of creating a duplicate. Null = not a
// cloud file yet.
let cloudRowId: number | null = null;
let cloudProjectName: string | null = null;

/** The current drawing as a recovery snapshot, or null when the drawing is clean
 *  (nothing worth recovering). Used by the local autosave (recovery.ts). */
function recoverySnapshot(): RecoverySnapshot | null {
  if (!state.docDirty) return null;
  try {
    return {
      drawing: drawingForSave(),
      savedProjectName: doc.savedProjectName, cloudRowId, cloudProjectName,
      savedAt: new Date().toISOString(),
      tabId: recoveryTabId(),
    };
  } catch { return null; }
}

// ---- Multi-tab coordination (web) ----------------------------------------
// A same-origin BroadcastChannel lets tabs (a) tell each other they're alive, so
// startup can distinguish a crashed tab's orphaned draft from one another tab is
// still editing, and (b) warn when the same cloud file is open in two tabs.
const tabChan: BroadcastChannel | null = (() => {
  try { return typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('stenalcad-tabs') : null; }
  catch { return null; }
})();
let sameFileWarned = false;

/** Answer the other tabs: alive, and the same cloud file open here. */
function wireTabChannel(): void {
  tabChan?.addEventListener('message', (ev: MessageEvent) => {
    const m = ev.data as { type?: string; from?: string; to?: string; cloudRowId?: number | null } | null;
    if (!m || typeof m !== 'object') return;
    const me = recoveryTabId();
    if (m.type === 'ping') {
      tabChan!.postMessage({ type: 'alive', from: me, cloudRowId });
    } else if (m.type === 'using' && m.from !== me) {
      // Another tab just opened a cloud file; warn if it's the one we have open.
      if (cloudRowId != null && m.cloudRowId === cloudRowId) {
        warnSameFile();
        tabChan!.postMessage({ type: 'sameFile', from: me, to: m.from });
      }
    } else if (m.type === 'sameFile' && m.to === me) {
      warnSameFile();
    }
  });
}

/** The set of currently-alive tab ids (ping + collect replies briefly). Empty
 *  when BroadcastChannel is unavailable. */
function findAliveTabs(): Promise<Set<string>> {
  return new Promise((resolve) => {
    if (!tabChan) { resolve(new Set()); return; }
    const alive = new Set<string>();
    const onMsg = (ev: MessageEvent): void => {
      const m = ev.data as { type?: string; from?: string } | null;
      if (m && m.type === 'alive' && m.from) alive.add(m.from);
    };
    tabChan.addEventListener('message', onMsg);
    tabChan.postMessage({ type: 'ping', from: recoveryTabId() });
    setTimeout(() => { tabChan!.removeEventListener('message', onMsg); resolve(alive); }, 400);
  });
}

/** Announce that this tab now has a cloud file open, so any other tab already on
 *  it warns (and warns us back). Call whenever cloudRowId becomes a real file. */
function announceCloudFile(): void {
  sameFileWarned = false; // a new file — allow a fresh warning
  if (tabChan && cloudRowId != null) tabChan.postMessage({ type: 'using', from: recoveryTabId(), cloudRowId });
}

function warnSameFile(): void {
  if (sameFileWarned) return;
  sameFileWarned = true;
  ensureCloudStyles();
  const bar = document.createElement('div'); bar.className = 'recovery-bar';
  const msg = document.createElement('span'); msg.className = 'msg';
  msg.textContent = tr('This project is open in another tab — saving from both may overwrite each other.');
  const ok = document.createElement('button'); ok.className = 'primary'; ok.textContent = tr('OK');
  ok.addEventListener('click', () => bar.remove());
  bar.append(msg, ok);
  document.body.appendChild(bar);
  setTimeout(() => { if (bar.isConnected) bar.remove(); }, 12000);
}

/** Install a recovered draft as the working drawing: like opening a file, but the
 *  result is marked unsaved (it never reached disk/cloud) and its cloud identity
 *  is preserved so a later Save updates the right file. */
function restoreRecovery(snap: RecoverySnapshot): void {
  applyLoadedDrawing(snap.drawing, snap.savedProjectName ?? snap.cloudProjectName ?? '');
  doc.savedProjectName = snap.savedProjectName; // applyLoadedDrawing set it to the display name
  cloudRowId = snap.cloudRowId;
  cloudProjectName = snap.cloudProjectName;
  state.docDirty = true; updateTitle(); // recovered work is unsaved
  updateTitle();
  announceCloudFile(); // let other tabs on this file know
  noteRecoveryChange(); // re-persist under this session
}

/** If an unsaved-work draft is recoverable, show a bar offering to restore or
 *  discard it (non-destructive: the app keeps its normal blank state until the
 *  user chooses Restore). Recoverable = this tab's own draft (a normal reload) or
 *  a draft orphaned by a tab that is no longer alive (a crash). A draft still
 *  owned by another live tab is left alone so the two tabs don't fight over it. */
async function maybeOfferRecovery(): Promise<void> {
  const all = (await listAllDrafts()).filter((d) => d && d.drawing);
  // Garbage-collect drafts older than a week: an orphan left by a crashed/closed
  // tab is a multi-MB snapshot that would otherwise linger in IndexedDB forever.
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = all.filter((d) => (Date.parse(d.savedAt) || 0) >= cutoff);
  for (const d of all) if (!recent.includes(d) && d.tabId) void deleteDraft(d.tabId);

  const me = recoveryTabId();
  const candidates: RecoverySnapshot[] = [];
  const mine = recent.find((d) => d.tabId === me);
  if (mine) candidates.push(mine); // this tab's own draft (a normal reload)
  const others = recent.filter((d) => d.tabId !== me);
  if (others.length && tabChan) {
    const alive = await findAliveTabs();
    for (const d of others) if (!alive.has(d.tabId)) candidates.push(d); // owner tab is gone (a crash)
  }
  if (!candidates.length) return;
  candidates.sort((a, b) => (a.savedAt < b.savedAt ? 1 : a.savedAt > b.savedAt ? -1 : 0)); // newest first
  offerRecovery(candidates[0]);
}

function offerRecovery(snap: RecoverySnapshot): void {
  ensureCloudStyles();
  const bar = document.createElement('div'); bar.className = 'recovery-bar';
  const loc = ({ en: 'en-GB', it: 'it-IT', fr: 'fr-FR' } as Record<string, string>)[state.drawing.language ?? 'it'] ?? 'en-GB';
  const when = new Date(snap.savedAt);
  const timeStr = isNaN(when.getTime()) ? '' : when.toLocaleString(loc, { dateStyle: 'medium', timeStyle: 'short' });
  const nm = snap.savedProjectName || snap.cloudProjectName;
  const msg = document.createElement('span'); msg.className = 'msg';
  msg.textContent = tr('Unsaved work was recovered') + (nm ? ' — “' + nm + '”' : '') + (timeStr ? ' (' + timeStr + ')' : '');
  const restore = document.createElement('button'); restore.className = 'primary'; restore.textContent = tr('Restore');
  const discard = document.createElement('button'); discard.textContent = tr('Discard');
  const close = (): void => bar.remove();
  // Consume the source draft either way: on Restore it's re-persisted under this
  // tab's own id; on Discard it's gone. (For an orphan, this frees its old slot.)
  restore.addEventListener('click', () => { const src = snap.tabId; restoreRecovery(snap); void deleteDraft(src); close(); });
  discard.addEventListener('click', () => { void deleteDraft(snap.tabId); close(); });
  bar.append(msg, restore, discard);
  document.body.appendChild(bar);
}

/** A serialisable copy of the drawing: PDF underlays drop their (large) base
 *  raster — only the PDF bytes are saved, and the raster is regenerated from them
 *  on load (regeneratePdfBaseRasters). Non-PDF images keep their dataUrl. */
function drawingForSave(): Drawing {
  const bgs = state.drawing.backgroundImages;
  if (!bgs || !bgs.some((b) => b.pdfData && b.dataUrl)) return state.drawing;
  return { ...state.drawing, backgroundImages: bgs.map((b) => (b.pdfData ? { ...b, dataUrl: '' } : b)) };
}

/** File ▸ Save (Ctrl+S) writes back to the project's own file; Save As
 *  (Ctrl+Shift+S), or a project that was never saved, asks where. */
export async function saveProject(saveAs = false): Promise<void> {
  state.drawing.activeView = state.viewTab; // remember the current tab
  const name = await platform.saveProject(drawingForSave(), saveAs);
  if (name) { doc.savedProjectName = name; state.docDirty = false; updateTitle(); clearRecovery(); } // saved → clean
}

/** Rebuild the base raster of any PDF underlay that was saved without one. */
export async function regeneratePdfBaseRasters(): Promise<void> {
  let any = false;
  for (const b of state.drawing.backgroundImages ?? []) {
    if (b.pdfData && !b.dataUrl) {
      try { b.dataUrl = (await rasterizePdfPage(b.pdfData, b.pdfPage ?? 1)).dataUrl; any = true; } catch { /* pdf.js unavailable — underlay stays blank until reopened */ }
    }
  }
  if (any) { redrawBackdrop(); invalidate(); }
}

/** File ▸ Export to DXF. When an imported DXF plan is present, ask whether to
 *  include it; then build the DXF and save it via a native dialog. */
/** The default name of an exported file or print job: the drawing's properties as
 *  "DATE PROJECTNO CUSTOMER VERSION", or `fallback` when none of them is set. */
export function exportName(fallback: string): string {
  return exportBaseName(state.drawing.meta, fallback);
}

async function exportToDxf(): Promise<void> {
  let includeBackground = false;
  const hasBg = !!(state.drawing.background && state.drawing.background.entities.length);
  if (hasBg) {
    const choice = await platform.askDxfInclude(); // 0 include, 1 exclude, 2 cancel
    if (choice !== 0 && choice !== 1) return;
    includeBackground = choice === 0;
  }
  // Skip categories a discipline toggle currently hides, so the export matches the view.
  const hiddenCategories = new Set((Object.keys(CATEGORY_DISCIPLINE) as ProductCategory[]).filter(isDisciplineHidden));
  const dxf = drawingToDxf(state.drawing, {
    layers: layers(), includeBackground, hiddenCategories, showCeilings: state.showCeilings, sectionSources: sectionSources(), rcpPaint: rcpPaintOf,
    lights: state.disciplineFilters.lighting !== false, floorings: state.disciplineFilters.flooring !== false, drainOf: drainInstance,
    connections: { schedule: state.showSchedule, filters: state.disciplineFilters },
  });
  const saved = await platform.exportDxf(dxf, exportName(state.drawing.fileName || doc.savedProjectName || 'drawing'));
  if (saved) flashStatus(tr('Exported to DXF.'));
}

/** Start a fresh, empty drawing (File ▸ New Drawing). Undoable. */
export async function newDrawing(): Promise<void> {
  if (!(await ensureSaved())) return;
  pushHistory();
  doc.savedProjectName = null; // a fresh, unsaved project
  cloudRowId = null; cloudProjectName = null;
  void platform.forgetProjectPath?.(); // its next Save asks where
  partsTab.currentLayerId = null;
  state.drawing = {
    background: null,
    backgroundImage: null,
    backgroundImages: [],
    instances: [],
    polygons: [],
    walls: [],
    dimensions: [],
    connections: [],
    units: 'mm',
    enteredNames: true,
  };
  leaveEdit('underlay');
  resetViewTabs();
  bumpBackground();
  updateTitle();
  deselectAll();
  setTool('select');
  state.docDirty = false; // a fresh empty drawing has nothing worth guarding
  updateTitle(); // no `*`
  clearRecovery(); // discard any recovered-work draft
  renderProperties();
  invalidate();
}

/** Prompt about unsaved changes (0 = Save, 1 = Don't Save, 2 = Cancel), falling
 *  back to a plain confirm if the host has no dialog. */
async function askUnsaved(): Promise<number> {
  if (platform.confirmUnsaved) return platform.confirmUnsaved();
  return (await showConfirm(tr('This drawing has unsaved changes. Discard them?'), { ok: 'Discard', danger: true })) ? 1 : 2;
}

/** Guard before any action that would discard the current drawing (open / new /
 *  close). Returns true when it is safe to proceed: no unsaved changes, the user
 *  chose Don't Save, or the save completed. Returns false to abort (Cancel, or a
 *  cancelled save). */
async function ensureSaved(): Promise<boolean> {
  if (!state.docDirty) return true;
  const choice = await askUnsaved();
  if (choice === 2) return false;                          // Cancel
  if (choice === 0) { await saveProject(); return !state.docDirty; } // Save (a cancelled save leaves it dirty)
  clearRecovery();                                         // Don't Save: nothing to offer back
  return true;
}

/** File ▸ Open Recent (desktop): the same as Open, straight to that file. */
async function openRecentProject(path: string): Promise<void> {
  if (!platform.openProjectPath || !(await ensureSaved())) return;
  const res = await platform.openProjectPath(path);
  if (!res) { void showAlert(`${tr('Could not read the drawing.')}\n${path}`); return; }
  cloudRowId = null; cloudProjectName = null;
  applyLoadedDrawing(res.drawing, res.name);
  clearRecovery();
}

export async function openProject(): Promise<void> {
  if (!(await ensureSaved())) return;
  const res = await platform.openProject();
  if (!res) return;
  cloudRowId = null; cloudProjectName = null; // a disk project isn't the loaded cloud file
  applyLoadedDrawing(res.drawing, res.name);
  clearRecovery(); // opened a clean file → drop any prior draft
}

// ---- Cloud projects (web build; server-mediated Save/Load) ----

/** The cloud-gallery preview of what's on screen: the active SHEET (as it prints)
 *  when a sheet is open, otherwise a 256×256 preview of the model drawing. PNG or
 *  JPEG, whichever encodes smaller; '' for an empty model with no sheet open. */
async function cloudPreviewDataUrl(): Promise<string> {
  const sh = activeSheet();
  if (sh) return sheetPreviewDataUrl(sh);
  const bounds = modelBoundsOrNull();
  if (!bounds) return '';
  return renderPreview(state.drawing, bounds, {
    showHatches: state.showHatches,
    showBeams: state.showBeams,
    showCeilings: state.showCeilings,
    disciplineFilters: state.disciplineFilters,
    backgroundImgs: bgImageMap(),
    paint: layers().paint,
    hideImported: importedHidden(),
  });
}

let cloudStylesInstalled = false;
function ensureCloudStyles(): void {
  if (cloudStylesInstalled) return;
  cloudStylesInstalled = true;
  const s = document.createElement('style');
  s.textContent = `
  /* Both notification bars are GOLD — the app's accent (#ffd166, the same amber a
     fresh selection draws in). Each appears over a dark canvas and warns about
     work that could be lost (a recovered draft whose Discard throws it away; a
     file open in two tabs that may overwrite itself), so neither may read as
     chrome. Dark ink on gold, and the primary button inverts to stay the loudest
     thing in the bar. */
  .recovery-bar { position:fixed; top:14px; left:50%; transform:translateX(-50%); z-index:100001;
    display:flex; align-items:center; gap:12px; padding:10px 14px; border-radius:8px;
    background:#ffd166; color:#2b2b2b; font:13px system-ui,sans-serif;
    border:1px solid #c8912a; box-shadow:0 8px 30px rgba(0,0,0,.55); }
  .recovery-bar .msg { max-width:52vw; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .recovery-bar button { padding:5px 12px; border-radius:5px; border:1px solid #b8862a;
    background:rgba(0,0,0,.10); color:#2b2b2b; font:inherit; cursor:pointer; }
  .recovery-bar button:hover { background:rgba(0,0,0,.18); }
  .recovery-bar button.primary { background:#2b2b2b; border-color:#2b2b2b; color:#ffd166; }
  .recovery-bar button.primary:hover { background:#141414; }
  .cloud-modal-overlay, .cloud-gallery-overlay { position:fixed; inset:0; z-index:100000;
    background:rgba(0,0,0,.5); display:flex; }
  .cloud-modal-overlay { align-items:center; justify-content:center; }
  .cloud-modal-title { font-weight:600; margin-bottom:12px; }
  .cloud-modal-actions { display:flex; gap:8px; justify-content:flex-end; margin-top:16px; }
  .cloud-gallery-overlay { flex-direction:column; background:#181818; }
  .cloud-gallery-head { display:flex; align-items:center; gap:12px; padding:12px 18px;
    border-bottom:1px solid #333; color:#eee; font:14px system-ui,sans-serif; }
  .cloud-gallery-head .title { font-weight:600; font-size:16px; }
  .cloud-gallery-head input { flex:1; max-width:420px; }
  .cloud-gallery-head select.cloud-creator { max-width:220px; cursor:pointer; }
  .cloud-gallery-body { flex:1; overflow:auto; padding:8px 18px 40px; }
  .cloud-month { color:#bbb; font:600 13px system-ui,sans-serif; margin:20px 4px 10px; }
  .cloud-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:16px; }
  .cloud-tile { cursor:pointer; border-radius:8px; overflow:hidden; background:#242424;
    border:1px solid #333; position:relative; }
  .cloud-tile:hover { border-color:#3d7eff; }
  .cloud-tile img { display:block; width:100%; aspect-ratio:1/1; object-fit:contain; background:#fff; }
  .cloud-tile .cap { padding:7px 9px 2px; color:#ddd; font:13px system-ui,sans-serif; white-space:nowrap;
    overflow:hidden; text-overflow:ellipsis; }
  .cloud-tile .cloud-sub { padding:0 9px 8px; color:#8a8f98; font:12px system-ui,sans-serif;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .cloud-tile .del { position:absolute; top:6px; right:6px; width:24px; height:24px; border:0;
    border-radius:50%; background:rgba(0,0,0,.55); color:#fff; cursor:pointer; display:none;
    align-items:center; justify-content:center; font-size:15px; line-height:1; }
  .cloud-tile:hover .del { display:flex; }
  .cloud-tile .cloud-shared { position:absolute; top:6px; left:6px; min-width:24px; height:24px;
    border-radius:12px; background:rgba(0,0,0,.55); color:#fff; display:flex; align-items:center;
    justify-content:center; font-size:13px; line-height:1; }
  .cloud-empty { color:#999; padding:40px; text-align:center; }
  .cloud-foot { color:#999; padding:18px; text-align:center; min-height:8px; font-size:13px; }
  .cloud-menu { position:fixed; z-index:100001; background:#2b2b2b; border:1px solid #111;
    box-shadow:0 6px 20px rgba(0,0,0,.5); padding:4px 0; border-radius:6px; min-width:160px; }
  .cloud-menu button { display:block; width:100%; text-align:left; padding:6px 14px; border:0;
    background:transparent; color:#eee; font:13px system-ui,sans-serif; cursor:pointer; }
  .cloud-menu button:hover { background:#3d7eff; color:#fff; }
  .cloud-menu button.danger:hover { background:#c0392b; }
  .cloud-share { background:#2b2b2b; color:#eee; font:13px system-ui,sans-serif; padding:18px 20px;
    border-radius:8px; width:380px; box-shadow:0 8px 30px rgba(0,0,0,.5); display:flex; flex-direction:column; }
  .cloud-share .cloud-modal-title { font-weight:600; margin-bottom:10px; }
  .cloud-share input.search { padding:7px 9px; border-radius:5px; border:1px solid #555;
    background:#1f1f1f; color:#eee; font:inherit; margin-bottom:8px; }
  .cloud-share-list { max-height:320px; overflow:auto; border:1px solid #3a3a3a; border-radius:6px; }
  .cloud-share-row { display:flex; align-items:center; gap:10px; padding:7px 11px; cursor:pointer; }
  .cloud-share-row:hover { background:#333; }
  .cloud-share-row.disabled { opacity:.55; cursor:default; }
  .cloud-share-row .nm { flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .cloud-share-row .tag { font-size:11px; color:#8a8f98; }
  .cloud-share-remove { font-size:11px; color:#e0796f; background:transparent; border:1px solid #5a3a38; border-radius:4px; padding:2px 8px; cursor:pointer; }
  .cloud-share-remove:hover { background:#3a2a29; border-color:#e0796f; }
  .cloud-share-remove:disabled { opacity:.5; cursor:default; }
  `;
  document.head.appendChild(s);
}

/** A small modal text prompt. Resolves to the entered string, or null if cancelled. */
function promptText(title: string, label: string, initial = ''): Promise<string | null> {
  return showPrompt(title, label, initial);
}

/** A non-colliding name: if `name` is already taken, bump a `_NN` counter (two
 *  digits, from _02) to the next free integer GREATER than the current one. An
 *  existing `_NN` on the name is reused (its stem) and stepped up, never doubled:
 *  Casa → Casa_02, Casa_02 → Casa_03, Casa_05 → Casa_06. */
function proposeVersionedName(name: string, taken: Set<string>): string {
  if (!taken.has(name)) return name;
  const m = /^(.*?)_(\d+)$/.exec(name);
  const stem = m ? m[1] : name;
  const cur = m ? parseInt(m[2], 10) : 1; // the bare name is implicitly version 1
  const pad = (n: number): string => (n < 10 ? '0' + n : String(n));
  for (let n = cur + 1; n < cur + 100000; n++) { // next free integer above current
    const cand = `${stem}_${pad(n)}`;
    if (!taken.has(cand)) return cand;
  }
  return name;
}

/** Existing cloud projects whose name contains `term` — a bounded, server-side
 *  lookup (not the whole table) used for save-name collision + version proposals. */
async function cloudNamesLike(term: string): Promise<SavedProjectSummary[]> {
  if (!platform.listCloudProjects || !term) return [];
  try { return (await platform.listCloudProjects({ search: term, size: 200 })).projects; }
  catch { return []; }
}

/** Save to Cloud (Ctrl+S) updates the cloud file this drawing came from, with no
 *  question; Save to Cloud As (Ctrl+Shift+S), or a drawing never saved there,
 *  asks for a name. */
async function saveToCloud(saveAs = false): Promise<void> {
  if (!platform.saveCloudProject) { void showAlert(tr('Cloud saving is not available in this build.')); return; }
  if (!saveAs && cloudRowId != null && cloudProjectName) { await writeCloud(cloudRowId, cloudProjectName); return; }
  // Look up existing names to propose a free versioned name and to spot collisions.
  // Scoped to the name's stem (a bounded query), so this scales to a large table.
  const base = (cloudProjectName ?? doc.savedProjectName ?? state.drawing.fileName ?? tr('Untitled')).trim() || tr('Untitled');
  const stemMatch = /^(.*?)_(\d+)$/.exec(base);
  const stem = stemMatch ? stemMatch[1] : base;
  const stemMatches = await cloudNamesLike(stem);
  const otherNames = new Set(stemMatches.filter((p) => p.rowId !== cloudRowId).map((p) => p.name));
  const suggested = proposeVersionedName(base, otherNames);

  const name = await promptText('Save to cloud', 'File name', suggested);
  if (name == null) return;
  const trimmed = name.trim();
  if (!trimmed) return;

  // Pick the target row:
  //  - same name as the current cloud file → update it (no warning);
  //  - matches a DIFFERENT writable project → warn, then overwrite that one;
  //  - otherwise → create a new file.
  let targetRowId: number | undefined;
  if (cloudRowId != null && trimmed === cloudProjectName) {
    targetRowId = cloudRowId;
  } else {
    const sameName = await cloudNamesLike(trimmed);
    const collide = sameName.find((p) => p.name === trimmed && p.rowId !== cloudRowId && p.canWrite);
    if (collide) {
      if (!(await showConfirm(tr('A project with this name already exists. Overwrite it?') + '\n\n“' + trimmed + '”', { ok: 'Overwrite', danger: true }))) return;
      targetRowId = collide.rowId;
    }
  }

  await writeCloud(targetRowId, trimmed);
}

/** Write the drawing to a cloud row (a new one when `rowId` is undefined). */
async function writeCloud(targetRowId: number | undefined, trimmed: string): Promise<void> {
  if (!platform.saveCloudProject) return;
  document.body.style.cursor = 'progress';
  try {
    const summary = await platform.saveCloudProject({
      rowId: targetRowId, name: trimmed, drawing: drawingForSave(), preview: await cloudPreviewDataUrl(),
    });
    cloudRowId = summary.rowId;
    cloudProjectName = summary.name;
    doc.savedProjectName = summary.name;
    state.docDirty = false;
    updateTitle();
    clearRecovery(); // saved → nothing to recover
    announceCloudFile(); // (re)assert which file this tab has open
  } catch (e) {
    void showAlert(tr('Cloud save failed') + ': ' + (e instanceof Error ? e.message : String(e)));
  } finally {
    document.body.style.cursor = '';
  }
}

async function loadCloudInto(summary: SavedProjectSummary, dismiss: () => void): Promise<void> {
  if (!platform.loadCloudProject) return;
  if (!(await ensureSaved())) return; // guard the current drawing's unsaved changes
  document.body.style.cursor = 'progress';
  try {
    const full = await platform.loadCloudProject(summary.rowId);
    dismiss();
    cloudRowId = full.rowId;
    cloudProjectName = full.name;
    applyLoadedDrawing(full.drawing, full.name);
    clearRecovery(); // loaded a clean cloud file → drop any prior draft
    announceCloudFile(); // warn if another tab already has this file open
  } catch (e) {
    void showAlert(tr('Cloud load failed') + ': ' + (e instanceof Error ? e.message : String(e)));
  } finally {
    document.body.style.cursor = '';
  }
}

/** Month header label for grouping, e.g. "July 2026", in the drawing's language. */
function monthLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return tr('Unknown date');
  const loc = ({ en: 'en-GB', it: 'it-IT', fr: 'fr-FR' } as Record<string, string>)[state.drawing.language ?? 'it'] ?? 'en-GB';
  return d.toLocaleDateString(loc, { year: 'numeric', month: 'long' });
}

// Session cache of the cad_users directory (id + name), so the gallery creator
// filter and the share dialog don't refetch it from the server on every open. A
// short TTL keeps a newly-added user appearing without a reload.
let cadUsersCache: { at: number; users: CadUser[] } | null = null;
async function cadUsersCached(): Promise<CadUser[]> {
  if (!platform.listCadUsers) return [];
  if (cadUsersCache && Date.now() - cadUsersCache.at < 5 * 60 * 1000) return cadUsersCache.users;
  const users = await platform.listCadUsers();
  cadUsersCache = { at: Date.now(), users };
  return users;
}

/** Share dialog: pick users (from the cad_users directory) to add to a project's
 *  acl. Users already in the acl are shown checked+disabled. `onShared` runs after
 *  a successful share so the gallery can refresh. */
async function openSharePopup(summary: SavedProjectSummary, onShared: () => void): Promise<void> {
  if (!platform.listCadUsers || !platform.shareCloudProject) return;
  ensureCloudStyles();
  const overlay = document.createElement('div'); overlay.className = 'cloud-modal-overlay'; overlay.dataset.modal = '';
  const box = document.createElement('div'); box.className = 'cloud-share';
  const title = document.createElement('div'); title.className = 'cloud-modal-title';
  title.textContent = tr('Share') + ' “' + summary.name + '”';
  const search = document.createElement('input'); search.className = 'search'; search.type = 'search'; search.placeholder = tr('Search users…');
  const list = document.createElement('div'); list.className = 'cloud-share-list'; list.textContent = tr('Loading…');
  const actions = document.createElement('div'); actions.className = 'cloud-modal-actions';
  const cancel = document.createElement('button'); cancel.textContent = tr('Cancel'); cancel.dataset.role = 'cancel';
  const ok = document.createElement('button'); ok.className = 'primary'; ok.textContent = tr('Share'); ok.dataset.role = 'confirm';
  actions.append(cancel, ok);
  box.append(title, search, list, actions);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  const close = (): void => overlay.remove();
  cancel.addEventListener('click', close);

  let users: CadUser[] = [];
  try { users = await cadUsersCached(); }
  catch (e) { list.textContent = tr('Could not load users') + ': ' + (e instanceof Error ? e.message : String(e)); return; }

  let already = new Set(summary.acl);   // ids that already have access
  const selected = new Set<string>();   // newly picked ids (name shown, id kept)
  const owner = summary.firstUser;      // the file's creator — always keeps access
  // The creator (or an `edit` user) may revoke other members' access here.
  const canManage = (!!myUserId() && owner === myUserId()) || canEditStandards();
  const renderRows = (q: string): void => {
    const query = q.trim().toLowerCase();
    list.innerHTML = '';
    const shown = users.filter((u) => !query || u.nome.toLowerCase().includes(query) || u.id.toLowerCase().includes(query));
    if (!shown.length) { const e = document.createElement('div'); e.className = 'cloud-share-row disabled'; e.textContent = tr('No users found.'); list.appendChild(e); return; }
    for (const u of shown) {
      const row = document.createElement('label'); row.className = 'cloud-share-row';
      const cb = document.createElement('input'); cb.type = 'checkbox';
      const has = already.has(u.id);
      if (has) { cb.checked = true; cb.disabled = true; row.classList.add('disabled'); }
      else { cb.checked = selected.has(u.id); }
      cb.addEventListener('change', () => { if (cb.checked) selected.add(u.id); else selected.delete(u.id); });
      const nm = document.createElement('span'); nm.className = 'nm'; nm.textContent = u.nome || u.id;
      row.append(cb, nm);
      if (has) {
        const isOwner = u.id === owner;
        const t = document.createElement('span'); t.className = 'tag';
        t.textContent = isOwner ? tr('owner') : tr('has access'); row.appendChild(t);
        // Owner/edit can revoke any member except the owner.
        if (canManage && !isOwner && platform.unshareCloudProject) {
          row.classList.remove('disabled'); // keep a revocable member fully legible
          const rm = document.createElement('button'); rm.type = 'button'; rm.className = 'cloud-share-remove'; rm.textContent = tr('Remove');
          rm.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); void (async () => {
            rm.disabled = true;
            try {
              const updated = await platform.unshareCloudProject!(summary.rowId, [u.id]);
              summary.acl = updated.acl; already = new Set(updated.acl);
              renderRows(search.value); onShared();
            } catch (err) { rm.disabled = false; void showAlert(tr('Remove failed') + ': ' + (err instanceof Error ? err.message : String(err))); }
          })(); });
          row.appendChild(rm);
        }
      }
      list.appendChild(row);
    }
  };
  search.addEventListener('input', () => renderRows(search.value));
  renderRows('');

  ok.addEventListener('click', () => { void (async () => {
    const ids = [...selected].filter((id) => !already.has(id));
    if (!ids.length) { close(); return; }
    ok.disabled = true;
    try {
      const updated = await platform.shareCloudProject!(summary.rowId, ids);
      summary.acl = updated.acl; // reflect the new acl in the gallery model
      close();
      onShared();
    } catch (e) {
      ok.disabled = false;
      void showAlert(tr('Share failed') + ': ' + (e instanceof Error ? e.message : String(e)));
    }
  })(); });
}

async function openCloudGallery(): Promise<void> {
  if (!platform.listCloudProjects) { void showAlert(tr('Cloud loading is not available in this build.')); return; }
  ensureCloudStyles();
  const overlay = document.createElement('div');
  overlay.className = 'cloud-gallery-overlay';
  overlay.dataset.modal = '';
  const head = document.createElement('div'); head.className = 'cloud-gallery-head';
  const title = document.createElement('div'); title.className = 'title'; title.textContent = tr('Load from cloud');
  const search = document.createElement('input'); search.type = 'search'; search.placeholder = tr('Search…');
  // `commerciale` users see everyone's projects; give them a creator filter that
  // defaults to their own files (with an "All creators" option to see everyone).
  const showCreatorFilter = amCommerciale() && !!platform.listCadUsers;
  const myCreatorId = myUserId();
  const myManagerId = myAreaManagerId();
  // Only commerciale users get the filter; it defaults to their own files. For
  // everyone else there is no filter (they'd otherwise hide files shared with them).
  let creatorFilter = showCreatorFilter ? myCreatorId : '';
  const creatorSel = document.createElement('select'); creatorSel.className = 'cloud-creator';
  const allOpt = document.createElement('option'); allOpt.value = ''; allOpt.textContent = tr('All creators');
  creatorSel.appendChild(allOpt);
  // Pin the current user as the default-selected option, before the directory loads.
  if (showCreatorFilter && myCreatorId) {
    const meOpt = document.createElement('option'); meOpt.value = myCreatorId;
    meOpt.textContent = (platform.currentUserName ? platform.currentUserName() : '') || myCreatorId;
    creatorSel.appendChild(meOpt);
    creatorSel.value = myCreatorId;
  }
  const close = document.createElement('button'); close.textContent = tr('Close'); close.dataset.role = 'cancel';
  if (showCreatorFilter) head.append(title, search, creatorSel, close);
  else head.append(title, search, close);
  const body = document.createElement('div'); body.className = 'cloud-gallery-body';
  body.textContent = tr('Loading…');
  overlay.append(head, body);
  document.body.appendChild(overlay);

  const dismiss = (): void => { overlay.remove(); };
  close.addEventListener('click', dismiss);

  // Server-side paging: fetch one page at a time (newest-edited first), ordered,
  // searched and creator-filtered by the server, and append on scroll — so a large
  // table is never loaded (or rendered) all at once.
  const PAGE = 60;
  let items: SavedProjectSummary[] = [];
  let page = 0, hasMore = true, loading = false;
  let epoch = 0; // bumped on reset(); an in-flight page from an older query is discarded
  let queryStr = '';
  let currentKey = ''; // for month grouping continued across appended pages
  let grid: HTMLElement | null = null;
  const footer = document.createElement('div'); footer.className = 'cloud-foot'; // status + scroll sentinel

  function clearView(): void { body.innerHTML = ''; currentKey = ''; grid = null; }

  // Directory names for the shared badge's tooltip. Loaded in the background (and
  // cached for the share dialog), so the gallery never waits on it — the badge
  // itself is drawn from the acl alone and only its tooltip gets richer.
  let userNames: Map<string, string> | null = null;
  function shareTip(ids: string[]): string {
    if (!userNames) return tr('Shared with other people');
    return tr('Shared with') + ': ' + ids.map((id) => userNames!.get(id) ?? id).join(', ');
  }
  function refreshShareTips(): void {
    body.querySelectorAll<HTMLElement>('.cloud-shared').forEach((el) => {
      el.title = shareTip((el.dataset.with ?? '').split(',').filter(Boolean));
    });
  }
  if (platform.listCadUsers) {
    void cadUsersCached()
      .then((us) => { userNames = new Map(us.map((u) => [u.id, u.nome || u.id])); refreshShareTips(); })
      .catch(() => { /* the badge still shows; only the names in its tooltip are missing */ });
  }

  function tileFor(p: SavedProjectSummary): HTMLElement {
    const tile = document.createElement('div'); tile.className = 'cloud-tile'; tile.title = p.name;
    const img = document.createElement('img');
    if (p.preview) img.src = p.preview; else img.alt = '';
    const cap = document.createElement('div'); cap.className = 'cap'; cap.textContent = p.name;
    tile.append(img, cap);
    if (p.lastUserName) {
      const sub = document.createElement('div'); sub.className = 'cloud-sub';
      sub.textContent = p.lastUserName; sub.title = tr('Last edited by') + ' ' + p.lastUserName;
      tile.appendChild(sub);
    }
    // Anyone in the acl besides me means this file is not mine alone. Badge it, so
    // a shared project reads as such at a glance instead of only from the share
    // dialog. My area manager does NOT count: the server adds it to everything I
    // create, so counting it would badge every project I own and say nothing.
    const others = p.acl.filter((id) => id && id !== myCreatorId && id !== myManagerId);
    if (others.length) {
      const sh = document.createElement('div'); sh.className = 'cloud-shared'; sh.textContent = '👥';
      sh.dataset.with = others.join(',');
      sh.title = shareTip(others);
      tile.appendChild(sh);
    }
    tile.addEventListener('click', () => { void loadCloudInto(p, dismiss); });
    tile.addEventListener('contextmenu', (ev) => { ev.preventDefault(); openTileMenu(ev, p); });
    if (p.canWrite && platform.deleteCloudProject) {
      const del = document.createElement('button'); del.className = 'del'; del.textContent = '×'; del.title = tr('Delete');
      del.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (!(await showConfirm(tr('Delete this cloud project?') + '\n\n' + p.name, { ok: 'Delete', danger: true }))) return;
        try { await platform.deleteCloudProject!(p.rowId); items = items.filter((x) => x.rowId !== p.rowId); rebuild(); }
        catch (err) { void showAlert(tr('Delete failed') + ': ' + (err instanceof Error ? err.message : String(err))); }
      });
      tile.appendChild(del);
    }
    return tile;
  }

  // Append summaries to the grid, continuing the month grouping from the last page.
  function renderTiles(list: SavedProjectSummary[]): void {
    for (const p of list) {
      const key = monthLabel(p.lastEdited);
      if (key !== currentKey || !grid) {
        currentKey = key;
        const hdr = document.createElement('div'); hdr.className = 'cloud-month'; hdr.textContent = key;
        body.appendChild(hdr);
        grid = document.createElement('div'); grid.className = 'cloud-grid'; body.appendChild(grid);
      }
      grid!.appendChild(tileFor(p));
    }
  }

  function updateFooter(): void {
    if (loading) { footer.textContent = tr('Loading…'); return; }
    if (!items.length) {
      footer.textContent = queryStr ? tr('No projects match your search.')
        : creatorFilter ? tr('No projects for this user.')
          : tr('No saved projects yet.');
      return;
    }
    footer.textContent = '';
  }

  // Re-render everything loaded so far (after a delete / rename / leave).
  function rebuild(): void {
    clearView();
    renderTiles(items);
    body.appendChild(footer);
    updateFooter();
    fillIfNeeded();
  }

  // Keep loading while the content is too short to scroll (so paging can start).
  function fillIfNeeded(): void {
    if (!loading && hasMore && body.scrollHeight <= body.clientHeight + 40) void loadMore();
  }

  async function loadMore(): Promise<void> {
    if (loading || !hasMore) return;
    loading = true; updateFooter();
    const myEpoch = epoch;      // snapshot: a reset() during the await invalidates this load
    const wantPage = page + 1;  // only commit `page` on success, so a failed fetch retries the same page
    try {
      const res = await platform.listCloudProjects!({ page: wantPage, size: PAGE, search: queryStr || undefined, creator: creatorFilter || undefined });
      if (myEpoch !== epoch) return; // superseded by a newer query — discard this response
      page = wantPage; hasMore = res.hasMore;
      items.push(...res.projects);
      renderTiles(res.projects);
      body.appendChild(footer); // keep the sentinel last
      loading = false; updateFooter();
      fillIfNeeded();
    } catch (e) {
      if (myEpoch !== epoch) return;
      loading = false; hasMore = false;
      footer.textContent = tr('Could not load cloud projects') + ': ' + (e instanceof Error ? e.message : String(e));
    }
  }

  function reset(): void {
    epoch += 1; items = []; page = 0; hasMore = true; loading = false; clearView(); body.appendChild(footer); void loadMore();
  }

  body.addEventListener('scroll', () => {
    if (body.scrollTop + body.clientHeight >= body.scrollHeight - 240) void loadMore();
  });

  // Right-click a tile: Open, Rename, Share with… (opens the user picker) and
  // Delete. Rename/Share/Delete are only shown for files the user can write (an
  // acl member, or an edit user); Delete asks for confirmation first.
  function openTileMenu(ev: MouseEvent, p: SavedProjectSummary): void {
    document.querySelector('.cloud-menu')?.remove();
    const menu = document.createElement('div'); menu.className = 'cloud-menu';
    const item = (label: string, fn: () => void, danger = false): void => {
      const b = document.createElement('button'); b.textContent = label; if (danger) b.className = 'danger';
      b.addEventListener('click', () => { menu.remove(); fn(); }); menu.appendChild(b);
    };
    item(tr('Open'), () => { void loadCloudInto(p, dismiss); });
    if (p.canWrite && platform.renameCloudProject) item(tr('Rename…'), () => { void (async () => {
      const name = await promptText('Rename project', 'File name', p.name);
      if (name == null) return;
      const nm = name.trim();
      if (!nm || nm === p.name) return;
      try {
        const updated = await platform.renameCloudProject!(p.rowId, nm);
        p.name = updated.name;
        if (cloudRowId === p.rowId) { cloudProjectName = updated.name; doc.savedProjectName = updated.name; updateTitle(); }
        rebuild();
      } catch (e) { void showAlert(tr('Rename failed') + ': ' + (e instanceof Error ? e.message : String(e))); }
    })(); });
    if (p.canWrite && platform.shareCloudProject) item(tr('Share with…'), () => { void openSharePopup(p, () => rebuild()); });
    // A member (shared with, but not the owner) can remove their own access.
    const me = myUserId();
    if (me && p.firstUser !== me && p.acl.includes(me) && platform.unshareCloudProject) {
      item(tr('Leave sharing'), () => { void (async () => {
        if (!(await showConfirm(tr('Remove your access to this shared project?') + '\n\n' + p.name, { ok: 'Leave sharing', danger: true }))) return;
        try { await platform.unshareCloudProject!(p.rowId, [me]); items = items.filter((x) => x.rowId !== p.rowId); rebuild(); }
        catch (e) { void showAlert(tr('Could not leave sharing') + ': ' + (e instanceof Error ? e.message : String(e))); }
      })(); });
    }
    if (p.canWrite && platform.deleteCloudProject) item(tr('Delete'), () => { void (async () => {
      if (!(await showConfirm(tr('Delete this cloud project?') + '\n\n' + p.name, { ok: 'Delete', danger: true }))) return;
      try { await platform.deleteCloudProject!(p.rowId); items = items.filter((x) => x.rowId !== p.rowId); rebuild(); }
      catch (e) { void showAlert(tr('Delete failed') + ': ' + (e instanceof Error ? e.message : String(e))); }
    })(); }, true);
    if (!menu.childElementCount) return;
    document.body.appendChild(menu);
    const mw = menu.offsetWidth || 170, mh = menu.offsetHeight || 40;
    menu.style.left = Math.min(ev.clientX, window.innerWidth - mw - 6) + 'px';
    menu.style.top = Math.min(ev.clientY, window.innerHeight - mh - 6) + 'px';
    const off = (e: MouseEvent): void => { if (!(e.target as HTMLElement).closest('.cloud-menu')) { menu.remove(); document.removeEventListener('mousedown', off); } };
    setTimeout(() => document.addEventListener('mousedown', off), 0);
  }

  // Search runs server-side (so it spans the whole table, not just loaded pages);
  // debounce so each keystroke doesn't fire a request.
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  search.addEventListener('input', () => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { queryStr = search.value.trim(); reset(); }, 300);
  });
  creatorSel.addEventListener('change', () => { creatorFilter = creatorSel.value; reset(); });
  reset();
  search.focus();

  // Populate the creator dropdown from the user directory (names of all users).
  if (showCreatorFilter && platform.listCadUsers) {
    void (async () => {
      try {
        const users = [...await cadUsersCached()];
        users.sort((a, b) => (a.nome || a.id).localeCompare(b.nome || b.id));
        for (const u of users) {
          if (u.id === myCreatorId) continue; // already pinned as the default option
          const opt = document.createElement('option'); opt.value = u.id; opt.textContent = u.nome || u.id;
          creatorSel.appendChild(opt);
        }
      } catch { /* leave just "All creators" if the directory can't be loaded */ }
    })();
  }
}

/** What the document listens to: its menu entries, the Open / Save buttons, the
 *  window closing, the recovery snapshot and the other tabs on the same file. */
export function wireDocument(): void {
  platform.onMenu('menu:newDrawing', newDrawing);
  platform.onMenu('menu:openProject', openProject);
  platform.onMenu('menu:saveProject', () => { void saveProject(); });
  platform.onMenu('menu:saveProjectAs', () => { void saveProject(true); });
  platform.onMenu('menu:saveCloud', () => { void saveToCloud(); });
  platform.onMenu('menu:saveCloudAs', () => { void saveToCloud(true); });
  platform.onOpenRecent?.((path) => { void openRecentProject(path); });
  platform.onMenu('menu:loadCloud', () => { void openCloudGallery(); });
  platform.onMenu('menu:exportDxf', () => { void exportToDxf(); });
  $('es-open').addEventListener('click', () => { if (window.stenal) void openProject(); else void openCloudGallery(); });
  $('btn-open-project').addEventListener('click', openProject);
  if (window.stenal) {
    $('btn-save-project').addEventListener('click', () => { void saveProject(); });
  } else {
    // Web build: no offline (disk) project save — cloud is the only save path, so
    // the Save button saves to the cloud instead.
    const sb = $('btn-save-project');
    sb.addEventListener('click', () => { void saveToCloud(); });
    sb.setAttribute('title', tr('Save to cloud'));
  }
  // Desktop: the main process intercepts the window close and asks us to resolve
  // unsaved changes first; on OK we let the close proceed.
  platform.onMenu('menu:requestClose', () => { void (async () => { if (await ensureSaved()) await platform.confirmClose?.(); })(); });
  // Both builds autosave the working drawing locally (IndexedDB), so a crash, a
  // power cut, or on the web a reload / tab eviction, can offer to restore it
  // instead of losing it — the desktop's unsaved-changes prompt only guards a
  // CLOSE, never a crash.
  initRecovery(recoverySnapshot);
  // pagehide / visibilitychange fire more reliably than beforeunload (mobile),
  // so flush a final snapshot there. The periodic autosave is the real guard.
  window.addEventListener('pagehide', () => flushRecovery());
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushRecovery(); });
  void maybeOfferRecovery(); // offer to restore a draft left by a previous session
  wireTabChannel();
}
